// hooks/usePaperActions.js
import { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { BASE_API_URL } from "../config";

export default function usePaperActions() {
  const navigate = useNavigate();
  const [msMap, setMsMap] = useState({});
  const userGuid = localStorage.getItem("userGuid");
  const accessToken = localStorage.getItem("accessToken");

  const authHeader = useMemo(
    () => (accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
    [accessToken]
  );

  const prefetchMarkSchemes = useCallback(async (papers = []) => {
    const missing = papers
      .map((p) => p.guid || p.id)
      .filter((id) => id && !msMap[id]);

    await Promise.all(
      missing.map(async (id) => {
        try {
          const r = await axios.get(
            `${BASE_API_URL}/api/v1/marking-schemes/by-past-paper-guid/${id}`,
            { headers: authHeader }
          );
          const url = r?.data?.file_url;
          if (url) setMsMap((prev) => ({ ...prev, [id]: url }));
        } catch (_) {}
      })
    );
  }, [authHeader, msMap]);

  const downloadQP = useCallback((url) => {
    if (url) window.open(url, "_blank");
  }, []);

  const downloadMS = useCallback(
    (paperGuid) => {
      const url = msMap[paperGuid];
      if (url) window.open(url, "_blank");
    },
    [msMap]
  );

  const proceedToSolve = useCallback(
    async (attemptGuid) => {
      const detail = await axios.get(
        `${BASE_API_URL}/api/v1/user-paper-attempts/detail/${attemptGuid}`,
        { headers: authHeader }
      );

      const { subject_name, paper_name, paper_code, time_hour, time_min, paper_title, name } =
        detail.data || {};

      const parts = (paper_name || "").split(" ");
      const formattedPaperName =
        (parts.length >= 2 && paper_code)
          ? `Paper ${parts[1]}${String(paper_code).trim().slice(-1)} ${parts.slice(2).join(" ")}`.trim()
          : paper_name || paper_title || name || "Topical Paper";

      const qs = await axios.get(
        `${BASE_API_URL}/api/v1/user-paper-attempts/questions-with-attempt/${attemptGuid}`,
        { headers: authHeader }
      );

      navigate("/solve-paper", {
        state: {
          questions: qs.data,
          paper_Guid: attemptGuid,
          subject_name,
          formattedPaperName,
          time_hour,
          time_min,
        },
      });
    },
    [authHeader, navigate]
  );

  const solvePaper = useCallback(
    async (paperGuid, { setLoading, setShowInProgressModal, setError } = {}) => {
      try {
        setLoading?.(true);
        const attempt = await axios.post(
          `${BASE_API_URL}/api/v1/user-paper-attempts/`,
          {
            completion_status: "INCOMPLETE",
            start_date_time: new Date().toISOString(),
            updated_date_time: new Date().toISOString(),
            total_scored_marks: 0,
            user_guid: userGuid,
            past_paper_guid: paperGuid,
          },
          { headers: authHeader }
        );
        await proceedToSolve(attempt.data.guid);
      } catch (error) {
        if (error?.response?.data?.detail === "Existing attempt already in-progress") {
          setShowInProgressModal?.(true);
        } else {
          setError?.("There was an error trying to access this paper. Please try again");
        }
      } finally {
        setLoading?.(false);
      }
    },
    [authHeader, proceedToSolve, userGuid]
  );

  const solveTopicalPaper = useCallback(
    async (topicalPaperGuid, { setLoading, setShowInProgressModal, setError } = {}) => {
      try {
        setLoading?.(true);
        const attempt = await axios.post(
          `${BASE_API_URL}/api/v1/user-paper-attempts/`,
          {
            completion_status: "INCOMPLETE",
            start_date_time: new Date().toISOString(),
            updated_date_time: new Date().toISOString(),
            total_scored_marks: 0,
            user_guid: userGuid,
            topical_paper_guid: topicalPaperGuid,
          },
          { headers: authHeader }
        );
        await proceedToSolve(attempt.data.guid);
      } catch (error) {
        if (error?.response?.data?.detail === "Existing attempt already in-progress") {
          setShowInProgressModal?.(true);
        } else {
          setError?.("There was an error trying to access this paper. Please try again");
        }
      } finally {
        setLoading?.(false);
      }
    },
    [authHeader, proceedToSolve, userGuid]
  );

  return {
    prefetchMarkSchemes,
    downloadQP,
    downloadMS,
    solvePaper,
    solveTopicalPaper,
    /** Open an attempt the user already has (search results, recent papers). */
    openAttempt: proceedToSolve,
  };
}

